// Hideo - script editor: the styles the browser writes by itself. When Chrome joins two blocks of different kinds
// (Backspace or Delete between a paragraph, a quote, a heading or a list) or moves text into another block, it wraps
// the moved text in <span style="…"> with the look it had there: the page's text colour (rgb(243, 244, 246) of the dark
// theme, which stays almost white with the light theme), the page's or a heading's size (1.05rem, 16.8px…), the quote's
// grey and italic. tidyStyles takes those values away again: a value equal to the one the text already gets from its
// block, the page's text colour of either theme, a size of the page's own styles (the user's sizes are whole pixels).
// Properties the editor never keeps (not in CLEAN_POLICY.css, e.g. Safari's -webkit-tap-highlight-color) go at once,
// as they would at the next opening. The user's colours, sizes, fonts and bold/italic stay. Called when a script is
// shown and after the browser's own joins, drops and pastes (src/features/editor/surface/typing.ts).
import { CLEAN_POLICY } from './html'

/** The page's text colour (--g100 in src/styles/legacy.css), dark and light theme */
const PAGE_TEXT = new Set(['rgb(243, 244, 246)', 'rgb(24, 24, 27)'])
/** The sizes of the page's own styles: text and h4 1.05rem, h3 1.2rem, h2 1.6rem, h1 2.1rem (at 16 px) */
const PAGE_SIZE = /^(1\.05|1\.2|1\.6|2\.1)rem$|^(16\.8|19\.2|25\.6|33\.6)px$/
const PROPS = ['color', 'font-size', 'font-style', 'font-weight', 'font-family'] as const

/** The style properties the editor keeps, as a list (CLEAN_POLICY.css), read once */
const KEPT = [...CLEAN_POLICY.css]
const kept = (p: string) => CLEAN_POLICY.css.has(p) || KEPT.some(a => p.startsWith(a + '-'))   // a part of "font", "background"…

/** Computed weights may be words or numbers */
const weight = (v: string): string => ({ normal: '400', bold: '700' } as Record<string, string>)[v] || v

/** Removes from one styled span the values that only repeat its surroundings; true when the span changed */
function tidyOne(el: HTMLElement): boolean {
  const parent = el.parentElement
  if (!parent) return false
  let changed = false
  const decls = (el.getAttribute('style') || '').split(';'), ok = decls.filter(d => !d.trim() || kept(d.slice(0, d.indexOf(':')).trim().toLowerCase()))
  if (ok.length !== decls.length) { el.setAttribute('style', ok.join(';')); changed = true }
  const st = el.style, mine = getComputedStyle(el), around = getComputedStyle(parent)
  for (const p of PROPS) {
    const v = st.getPropertyValue(p)
    if (!v) continue
    const same = weight(mine.getPropertyValue(p)) === weight(around.getPropertyValue(p))
    const page = (p === 'color' && PAGE_TEXT.has(v.replace(/\s+/g, ' ').trim())) || (p === 'font-size' && PAGE_SIZE.test(v.trim()))
    if (same || page) { st.removeProperty(p); changed = true }
  }
  if (changed && !el.getAttribute('style')?.trim()) el.removeAttribute('style')
  return changed
}

/** Tidies the styled spans of root (a page or a block); a span left without attributes is unwrapped. The selection
 *  is kept on the same text. */
export function tidyStyles(root: ParentNode | null | undefined): void {
  if (!root || !root.querySelectorAll) return
  const spans = [...root.querySelectorAll<HTMLElement>('span[style]')].filter(tidyOne)
  const bare = spans.filter(el => !el.attributes.length)
  if (!bare.length) return
  const s = getSelection(), keep = s && s.rangeCount ? [s.anchorNode, s.anchorOffset, s.focusNode, s.focusOffset] as const : null
  for (const el of bare) el.replaceWith(...el.childNodes)
  if (keep && s && keep[0] && keep[2] && keep[0].isConnected && keep[2].isConnected && keep[0].nodeType === 3 && keep[2].nodeType === 3) {
    s.setBaseAndExtent(keep[0], keep[1], keep[2], keep[3])
  }
}

/** Elements that are blocks of the page (a top-level child that holds one of them is a wrapper, not a block) */
const BLOCK_TAGS = /^(P|DIV|H[1-6]|UL|OL|BLOCKQUOTE|PRE|DETAILS|TABLE|HR|SECTION|ARTICLE|HEADER|FOOTER|MAIN|NAV|ASIDE|FIGURE|ADDRESS|DL|CENTER)$/
/** Wrappers that may be opened: plain containers and the inline elements some pastes put around everything (Google
 *  Docs: <b id="docs-internal-guid…">); never a callout (div.callout, its paragraphs belong to it) */
const WRAPPERS = /^(DIV|SECTION|ARTICLE|HEADER|FOOTER|MAIN|NAV|ASIDE|CENTER|P|B|STRONG|I|EM|SPAN|FONT)$/
/** One element per block at the top of the page: a top-level wrapper that holds blocks (a heading and its paragraphs
 *  inside one <div>…) is opened, the text beside those blocks going into paragraphs of its own. Otherwise the handle,
 *  Duplicate, moves and the sheets would treat the whole wrapper as one block (user report, 30/10/2026). The
 *  selection stays on the same text (the nodes are moved, not copied). */
export function flattenBlocks(body: HTMLElement | null | undefined): void {
  if (!body) return
  for (let el = body.firstElementChild as HTMLElement | null; el;) {
    const next = el.nextElementSibling as HTMLElement | null
    if (WRAPPERS.test(el.tagName) && !el.classList.contains('callout') && [...el.children].some(c => BLOCK_TAGS.test(c.tagName))) {
      const parts: Node[] = []
      let run: HTMLElement | null = null
      for (const n of [...el.childNodes]) {
        if (n.nodeType === 1 && BLOCK_TAGS.test((n as Element).tagName)) { run = null; parts.push(n); continue }
        if (!run && n.nodeType === 3 && !n.textContent?.trim()) continue   // layout spaces between blocks
        if (!run) { run = document.createElement('p'); parts.push(run) }
        run.appendChild(n)
      }
      const first = parts[0] as HTMLElement | undefined
      el.replaceWith(...parts)
      el = first && first.nodeType === 1 ? first : next   // the opened blocks may be wrappers too
      continue
    }
    el = next
  }
}
